import { ChevronLeftIcon, ChevronRightIcon } from "lucide-react";
import { type MouseEvent, useId, useMemo, useRef, useState } from "react";
import { cn } from "../../utils";
import { Dropdown, useDropdownContext } from "../Dropdown";
import { Tooltip } from "../Tooltip";
import { Text } from "../Typography";
import { MenuContent } from "./MenuContent";
import { useMenuContext } from "./context";
import { isMenuOption, type MenuOption } from "./types";

type MenuOptionItemProps = {
  option: MenuOption;
  index: number;
};

export const MenuOptionItem = ({ option, index }: MenuOptionItemProps) => {
  const { onSelect, rtl, rootTriggerRef, portal } = useMenuContext();
  const {
    navigationRef,
    getItemProps,
    activeIndex,
    dismiss,
    contentId: parentContentId,
  } = useDropdownContext();

  const [open, setOpen] = useState<boolean>(false);
  const isActive = activeIndex === index;

  const optionId = `${parentContentId}-option-${index}`;
  const contentId = useId();

  const submenuTypeaheadListRef = useRef<(string | null)[]>([]);
  useMemo(() => {
    if (option.options) {
      submenuTypeaheadListRef.current = option.options
        .filter(isMenuOption)
        .map((opt) => opt.text);
    }
  }, [option.options]);

  const handleSelect = (e: MouseEvent<HTMLButtonElement>) => {
    e?.stopPropagation();

    if (option.options) {
      return;
    }

    onSelect(option.id);
    dismiss();

    setTimeout(() => {
      rootTriggerRef?.current?.focus();
    }, 0);
  };

  const triggerElement = (
    <button
      {...(option.dataAttributes || {})}
      id={optionId}
      role="menuitem"
      aria-disabled={option.disabled}
      aria-haspopup={option.options ? "menu" : undefined}
      aria-expanded={option.options ? open : undefined}
      className={cn(
        "flex w-full items-center gap-3 px-2 py-2 text-sm rounded-md focus:outline-none",
        !option.disabled &&
          "hover:bg-primary-500/5 focus:bg-primary-500/5 active:bg-primary-500/10",
        option.disabled && "text-neutral-200 cursor-not-allowed",
        option.disabled && Boolean(option.tooltip) && "pointer-events-none",
        option.className,
      )}
      disabled={option.disabled}
      ref={navigationRef(index)}
      {...(!option.options ? getItemProps?.() : {})}
      onClick={handleSelect}
      tabIndex={isActive ? 0 : -1}
    >
      {option.icon && (
        <span className="flex h-4 w-4 items-center justify-center">
          {option.icon}
        </span>
      )}
      <Text size="sm" className="flex-1 text-start">
        {option.text}
      </Text>
      {option.accessory && option.accessory}
      {option.options && (
        <span className="flex h-4 w-4 items-center justify-center">
          {rtl ? <ChevronLeftIcon size={14} /> : <ChevronRightIcon size={14} />}
        </span>
      )}
    </button>
  );

  const renderedItem =
    option.options && !option.disabled ? (
      <Dropdown
        trigger={triggerElement}
        content={<MenuContent options={option.options} />}
        triggerId={optionId}
        contentId={contentId}
        open={open}
        onOpenChange={setOpen}
        interactions={[
          "hover",
          "click",
          "dismiss",
          "listNavigation",
          "typeahead",
        ]}
        role="menu"
        placement={rtl ? "left-start" : "right-start"}
        offset={0}
        hoverOptions={{
          delay: { open: 100, close: 0 },
        }}
        listNavigationOptions={{
          nested: true,
          parentOrientation: "vertical",
          rtl,
        }}
        floatingFocusManager={{
          initialFocus: -1,
          returnFocus: true,
          restoreFocus: false,
          modal: false,
        }}
        ariaLabel={`${option.text} submenu`}
        typeaheadListRef={submenuTypeaheadListRef}
        portal={portal}
      />
    ) : (
      triggerElement
    );

  if (option.tooltip) {
    return (
      <Tooltip content={option.tooltip} placement={rtl ? "left" : "right"}>
        <span className="block w-full">{renderedItem}</span>
      </Tooltip>
    );
  }

  return renderedItem;
};
